import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';

import { CrmDateFormatKey, CrmDateFormatOption, formatCrmDateOptionPreview } from './crm-date-format-options';

@Component({
  selector: 'app-crm-date-format-select',
  template: `
    <div class="crm-date-format-select" [class.crm-date-format-select-open]="isOpen">
      <as-button
        type="secondary"
        [minimal]="true"
        size="small"
        aria-haspopup="listbox"
        [attr.aria-expanded]="isOpen"
        (click)="toggleMenu($event)">
        <i class="fas fa-calendar-alt"></i>
        <span class="crm-date-format-label">{{ selectedOption?.label }}</span>
        <span class="crm-date-format-preview">{{ selectedOption ? formatOptionDate(selectedOption) : '' }}</span>
        <i class="fa" [ngClass]="isOpen ? 'fa-angle-up' : 'fa-angle-down'"></i>
      </as-button>

      <div class="crm-date-format-panel" role="listbox" *ngIf="isOpen">
        <button
          type="button"
          class="crm-date-format-option"
          role="option"
          *ngFor="let option of options; trackBy: trackByOptionValue"
          [class.crm-date-format-option-active]="option.value === value"
          [attr.aria-selected]="option.value === value"
          (click)="selectOption(option)">
          <span class="crm-date-format-label">{{ option.label }}</span>
          <span class="crm-date-format-preview">{{ formatOptionDate(option) }}</span>
        </button>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: inline-block;
    }

    .crm-date-format-select {
      position: relative;
      font-family: Roboto, sans-serif;
    }

    .crm-date-format-option {
      display: grid;
      grid-template-columns: 125px 86px 8px;
      align-items: center;
      width: 100%;
      font-family: Roboto, sans-serif;
      font-size: 0.64rem;
      font-variant-numeric: tabular-nums;
      font-feature-settings: "tnum" 1;
      letter-spacing: 0;
      line-height: 1.35;
      text-align: left;
      text-transform: none;
      white-space: nowrap;
    }

    .crm-date-format-select as-button {
      display: inline-block;
    }

    .crm-date-format-panel {
      position: absolute;
      top: calc(100% + 8px);
      right: 0;
      min-width: 220px;
      padding: 8px;
      border: 1px solid #d1d5db;
      border-radius: 12px;
      background: #ffffff;
      box-shadow: 0 18px 36px rgba(15, 23, 42, 0.16);
      z-index: 1200;
    }

    .crm-date-format-option {
      border: 0;
      border-radius: 8px;
      background: transparent;
      color: #111827;
      cursor: pointer;
      font-size: 14px;
      line-height: 1.4;
      padding: 10px 12px;
      transition: background 0.2s ease, color 0.2s ease;
    }

    .crm-date-format-option:hover,
    .crm-date-format-option:focus {
      outline: none;
      background: #eff6ff;
      color: #0f4c81;
    }

    .crm-date-format-option-active {
      background: #eff6ff;
      color: #0f4c81;
      font-weight: 700;
    }

    .crm-date-format-option-active:hover,
    .crm-date-format-option-active:focus {
      background: #eff6ff;
      color: #0f4c81;
    }

    .crm-date-format-label,
    .crm-date-format-preview {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .crm-date-format-preview {
      justify-self: start;
    }
      span.crm-date-format-label {
      margin: 0 5px;
    }

    span.crm-date-format-preview {
      margin-right: 5px;
    }
  `]
})
export class CrmDateFormatSelectComponent {
  @Input() options: CrmDateFormatOption[] = [];
  @Input() previewDate = new Date();
  @Input() value: CrmDateFormatKey = 'Default';
  @Output() valueChange = new EventEmitter<CrmDateFormatKey>();

  isOpen = false;

  constructor(private elementRef: ElementRef<HTMLElement>) {
  }

  get selectedOption(): CrmDateFormatOption | undefined {
    return this.options.find((option) => option.value === this.value) || this.options[0];
  }

  toggleMenu(event: Event): void {
    event.stopPropagation();
    this.isOpen = !this.isOpen;
  }

  selectOption(option: CrmDateFormatOption): void {
    this.value = option.value;
    this.valueChange.emit(option.value);
    this.isOpen = false;
  }

  formatOptionDate(option: CrmDateFormatOption): string {
    return formatCrmDateOptionPreview(option, this.previewDate);
  }

  trackByOptionValue(index: number, option: CrmDateFormatOption): CrmDateFormatKey {
    return option.value;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;

    if (target && !this.elementRef.nativeElement.contains(target)) {
      this.isOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.isOpen = false;
  }
}
